import { Component, Inject, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ILevelAsset } from '@services/assets-data.service';
import { GlobalFunctions, imagePath, imgTypes } from '@global/GlobalFunctions';
import { takeUntil, distinctUntilChanged, filter } from 'rxjs/operators';
import { combineLatest, Subject } from 'rxjs';
import { FilesService } from '@services/files.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Account, IAccount } from '@services/accounts.service';

/**
 * Component
 */
@Component({
	selector: 'app-edit-level-asset',
	templateUrl: './edit-level-asset.component.html',
	styleUrls: ['./edit-level-asset.component.scss']
})
export class EditHierarchyAssetComponent implements OnInit, OnDestroy {

	/** Path to Assets Images */
	public imagePathAssets(): string {
		return imagePath(this.selectedAccountGUID, imgTypes.organisationassets).toLowerCase()
	}

	/** selectedAccount */
	private selectedAccount: IAccount;

	/** selectedAccountGUID */
	private selectedAccountGUID: string

	/** Update the Image */
	@Output() imageUpdated = new EventEmitter<any>();

	/** Update Image Instantly */
	// public numberRND: number = Math.random();
	/** new Image Selected */
	public newImage: boolean;

	/** Form Group */
	dialogForm: FormGroup;

	/** Enable the New Picture Button */
	public enablePicture: boolean = true;
	/** Enable the Camera Button */
	public enableCamera: boolean = true;
	/** Enable the Delete Button */
	public enableDelete: boolean = false;

	/** File */
	public file: any
	/** Filename */
	public fileName: string
	/** Loading */
	public loading: boolean
	/** shortLink */
	public shortLink: any

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	imageSrc: string | null = null; // This will hold the image URL for binding

	/**
	 * Default Constructor
	 * @param fb Form Builder
	 * @param dialogRef Referense to the Dialog box
	 * @param globalFunctions Global Functions
	 * @param filesService 
	 * @param siteSubscriptions 
	 * @param dialogData  object interface	 
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<EditHierarchyAssetComponent>,
		private globalFunctions: GlobalFunctions,
		private filesService: FilesService,
		private siteSubscriptions: siteSubscriptions,
		@Inject(MAT_DIALOG_DATA) public dialogData: ILevelAsset,
	) {

		combineLatest([
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([account]) => !!account)
		).subscribe({
			next: ([account]) => {
				this.selectedAccount = account;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.selectedAccountGUID = this.selectedAccount.GUID;
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false); }
		});

	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.dialogForm = this.fb.group({
			AssetID: [this.dialogData.AssetID || 0],
			Name: [this.dialogData.Name || '', Validators.required],
			Description: [this.dialogData.Description || ''],
			ImageURL: [this.dialogData.ImageURL || ''],
			Expanded: [this.dialogData.Expanded || false],
			ParentUID: [this.dialogData.ParentUID || ''],
			Type: [this.dialogData.Type || 1],
			Watch: [this.dialogData.Watch || false],
			DisplayOnImage: [this.dialogData.DisplayOnImage || false],
			FunctionalLocation: [this.dialogData.FunctionalLocation || ''],
			EquipmentCode: [this.dialogData.EquipmentCode || ''],
			ParentFunctionalLocation: [this.dialogData.ParentFunctionalLocation || ''],

		});

		this.imageSrc = this.imagePathAssets() + this.dialogData.AssetID + '?' + this.dialogData.Modified
	}

	/**
 * Page On Destroy Event Handler
 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	/**
	 * Save the form data and image if needed
	 */
	async onSaveClick(): Promise<void> {

		if (this.newImage && this.file) {
			try {
				const accountGUID = this.selectedAccountGUID.toLowerCase();
				const fileName = this.dialogData.AssetID.toString();
				const location = "assets";
				await this.filesService.saveImage(accountGUID, fileName, this.file, location); // wait for upload to complete
				this.newImage = false;
				this.file = undefined;
			} // Optional: handle upload failure (maybe prevent closing the dialog)
			catch (error) { return; }
		}

		// Now the upload is done, close the dialog with the form value
		this.dialogRef.close(this.dialogForm.value);
	}

	/**
	 * Delete Image
	 */
	deleteImg(): void {
		// Extract and store the file name
		this.fileName = "transparent.png";
		this.imageSrc = "";
		this.file = new File([""], "transparent.png", { type: "image/png" });
		this.dialogForm.markAsDirty();
		this.newImage = true;
	}

	/**
	 * New File deteced
	 * @param file File from child component
	 */
	onNewImage(file: File | null): void {

		/** Check for a valid file */
		this.file = file ? file : undefined;
		this.newImage = true;
		this.dialogForm.markAsDirty();
	}
}
